class AlertModal {
  constructor() {
    this.activeModals = 0
    this.modalStack = []
    this.escapeListener = this.handleEscapeKey.bind(this)
  }

  alert = (text, actionText = 'OK', type = 'info') => {
    return this.showModal(text, null, actionText, type)
  }

  confirm = (text, okText = 'OK', cancelText = 'Cancel', type = 'confirm') => {
    return this.showModal(text, okText, cancelText, type)
  }

  showModal = (text, okText, cancelText, type = 'info') => {
    const modalId = `modal-${Date.now()}-${Math.floor(Math.random() * 1000)}`

    // Create the modal HTML using a template string
    const modalHTML = `
      <div id="${modalId}" class="modal-background" style="
        background-color: rgba(0, 0, 0, 0.7);
        width: 100%;
        height: 100%;
        position: fixed;
        top: 0;
        left: 0;
        z-index: ${1000 + this.activeModals};
        display: flex;
        justify-content: center;
        align-items: center;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
      ">
        <div class="modal-content modal-${type}" style="
          background-color: white;
          width: 350px;
          max-width: 90vw;
          max-height: 90vh;
          overflow: auto;
          padding: 2rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          font-weight: 400;
          font-size: 14px;
          line-height: 1.5;
          border-radius: 4px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          transform: translateY(20px);
          transition: transform 0.2s ease-out;
          ${type ? `border-top: 4px solid ${this.getTypeColor(type)};` : ''}
        ">
          <div class="modal-text" style="margin-bottom: 20px;">
            ${text}
          </div>
          <div class="modal-actions" style="
            width: 100%;
            display: flex;
            justify-content: flex-end;
            margin-top: 15px;
            gap: 10px;
          ">
            ${okText
              ? `
              <button type="button" class="ok-button" style="
                min-width: 92px;
                padding: 0.5rem 1rem;
                background-color: black;
                border: none;
                border-radius: 3px;
                color: white;
                font-size: 14px;
                cursor: pointer;
              ">${okText}</button>
            `
              : ''}
            <button type="button" class="cancel-button" style="
              min-width: 92px;
              padding: 0.5rem 1rem;
              background-color: transparent;
              border: 1px solid #ccc;
              border-radius: 3px;
              color: black;
              font-size: 14px;
              cursor: pointer;
            ">${cancelText}</button>
          </div>
        </div>
      </div>
    `

    // Add to document
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = modalHTML
    const modal = tempDiv.firstElementChild
    document.body.appendChild(modal)

    // Get references to elements
    const content = modal.querySelector('.modal-content')
    const okButton = modal.querySelector('.ok-button')
    const cancelButton = modal.querySelector('.cancel-button')

    // Create promise to return
    const modalPromise = new Promise((resolve) => {
      // Track for stacking purposes
      this.activeModals++
      this.modalStack.push({
        id: modalId,
        element: modal,
        resolve,
      })

      // Add button event handlers
      if (okButton) {
        const okHandler = () => {
          this.removeModal(modalId)
          resolve(true)
        }
        okButton.addEventListener('click', okHandler, { once: true })

        // Add hover effects
        okButton.addEventListener('mouseover', () => {
          okButton.style.backgroundColor = '#333'
        })
        okButton.addEventListener('mouseout', () => {
          okButton.style.backgroundColor = 'black'
        })
      }

      const cancelHandler = () => {
        this.removeModal(modalId)
        resolve(false)
      }
      cancelButton.addEventListener('click', cancelHandler, { once: true })

      // Add hover effects to cancel button
      cancelButton.addEventListener('mouseover', () => {
        cancelButton.style.backgroundColor = '#f5f5f5'
      })
      cancelButton.addEventListener('mouseout', () => {
        cancelButton.style.backgroundColor = 'transparent'
      })

      // Add keyboard listeners if this is the first modal
      if (this.activeModals === 1) {
        document.addEventListener('keydown', this.escapeListener)
      }
    })

    // Focus the appropriate button (OK for confirms, Cancel for alerts)
    if (okButton) {
      okButton.focus()
    }
    else {
      cancelButton.focus()
    }

    // Animate in
    setTimeout(() => {
      modal.style.opacity = '1'
      content.style.transform = 'translateY(0)'
    }, 10)

    return modalPromise
  }

  getTypeColor = (type) => {
    const colors = {
      info: '#f5f6f8',
      success: '#4CAF50',
      warning: '#FF9800',
      error: '#F44336',
      confirm: '#607D8B',
    }
    return colors[type] || colors.info
  }

  removeModal = (modalId) => {
    const modalIndex = this.modalStack.findIndex(modal => modal.id === modalId)

    if (modalIndex === -1) return

    const modal = this.modalStack[modalIndex]

    // Animate out
    modal.element.style.opacity = '0'
    const content = modal.element.querySelector('.modal-content')
    content.style.transform = 'translateY(20px)'

    // Remove after animation
    setTimeout(() => {
      modal.element.remove()
      this.modalStack.splice(modalIndex, 1)
      this.activeModals--

      // Remove keyboard listener if no more modals
      if (this.activeModals === 0) {
        document.removeEventListener('keydown', this.escapeListener)
      }
    }, 200)
  }

  closeAll = () => {
    // Clone the stack to avoid modification during iteration
    const modals = [...this.modalStack]

    // Close each modal with a false result
    modals.forEach((modal) => {
      modal.resolve(false)
      this.removeModal(modal.id)
    })
  }

  handleEscapeKey = (event) => {
    if (event.key === 'Escape' && this.modalStack.length > 0) {
      // Close only the top-most modal
      const topModal = this.modalStack[this.modalStack.length - 1]
      topModal.resolve(false)
      this.removeModal(topModal.id)
    }
  }
}

// Types: 'info', 'success', 'warning', 'error', 'confirm'
const alertModal = new AlertModal()

const confirming = async (text, ok = 'OK', cancel = 'Cancel', type = 'confirm') => {
  return await alertModal.confirm(text, ok, cancel, type)
}

const alerting = async (text, actionText = 'OK', type = 'info') => {
  return await alertModal.alert(text, actionText, type)
}

// Additional helper functions for different alert types
const successAlert = async (text, actionText = 'OK') => {
  return await alertModal.alert(text, actionText, 'success')
}

const warningAlert = async (text, actionText = 'OK') => {
  return await alertModal.alert(text, actionText, 'warning')
}

const errorAlert = async (text, actionText = 'OK') => {
  return await alertModal.alert(text, actionText, 'error')
}

export { alertModal, confirming, alerting, successAlert, warningAlert, errorAlert }
